const ADMIN_API_BASE_URL = (process.env.REACT_APP_ADMIN_API_BASE_URL || "").replace(/\/+$/, "");

type AdminUrlCandidateOptions = {
  pathname?: string;
  baseUrl?: string;
  publicBasePath?: string;
};

type ApiErrorPayload = {
  msg?: string;
};

type ParsedApiPayload<T> = {
  contentType: string;
  data: (T & ApiErrorPayload) | null;
  isHtml: boolean;
  isJson: boolean;
  preview: string;
  rawText: string;
};

function normalizeBasePath(pathname: string) {
  const normalizedPath = String(pathname || "")
    .split(/[?#]/, 1)[0]
    .replace(/\/+$/, "");
  if (!normalizedPath || normalizedPath === "/") return "";
  return normalizedPath.startsWith("/") ? normalizedPath : `/${normalizedPath}`;
}

export function deriveAdminAppBasePath(pathname: string, explicitBasePath?: string) {
  const configuredBasePath = normalizeBasePath(explicitBasePath ?? process.env.PUBLIC_URL ?? "");
  if (configuredBasePath) {
    return configuredBasePath;
  }

  const normalizedPath = String(pathname || "")
    .split(/[?#]/, 1)[0]
    .replace(/\/+$/, "");
  const adminIndex = normalizedPath.toLowerCase().indexOf("/admin");
  if (adminIndex <= 0) return "";

  const adminSuffix = normalizedPath.slice(adminIndex);
  if (adminSuffix !== "/admin" && !adminSuffix.startsWith("/admin/")) {
    return "";
  }

  return normalizedPath.slice(0, adminIndex);
}

// 관리자 API 호출 경로를 환경 변수 기준 절대/상대 경로 후보로 정규화한다.
export function buildAdminUrlCandidates(path: string, options: AdminUrlCandidateOptions = {}) {
  const normalizedPath = path.startsWith("/") ? path : `/${path}`;
  const baseUrl = (options.baseUrl ?? ADMIN_API_BASE_URL).replace(/\/+$/, "");
  if (baseUrl) {
    return [`${baseUrl}${normalizedPath}`];
  }

  const pathname = options.pathname ?? (typeof window !== "undefined" ? window.location.pathname : "");
  const appBasePath = deriveAdminAppBasePath(pathname, options.publicBasePath).replace(/\/+$/, "");
  const candidates = [normalizedPath];

  if (appBasePath && normalizedPath.startsWith("/api/")) {
    candidates.push(`${appBasePath}${normalizedPath}`);
  }

  return candidates.filter((candidate, index) => candidates.indexOf(candidate) === index);
}

function formatTriedUrls(urls: string[]) {
  return urls.length > 0 ? ` 시도한 경로: ${urls.join(" -> ")}` : "";
}

function buildNonJsonApiError<T>(payload: ParsedApiPayload<T>, triedUrls: string[]) {
  const hint = ADMIN_API_BASE_URL
    ? "관리자 API 대신 HTML이 반환되었습니다. REACT_APP_ADMIN_API_BASE_URL 값이 관리자 백엔드를 가리키는지 확인해주세요."
    : "관리자 API 대신 HTML이 반환되었습니다. same-origin /api/admin/* 또는 서브경로 /api/admin/* 가 관리자 백엔드에 연결되는지 확인해주세요.";
  const previewText = payload.preview ? ` 응답 일부: ${payload.preview}` : "";
  return `${hint}${formatTriedUrls(triedUrls)}${previewText}`;
}

function shouldRetryApiMismatch<T>(response: Response, payload: ParsedApiPayload<T>) {
  if (payload.isHtml) return true;
  if (response.status === 404 && !payload.isJson) return true;
  return false;
}

// API 응답 본문을 JSON으로 읽고, 재시도 여부를 판단할 수 있게 메타데이터도 함께 보존한다.
async function readApiPayload<T>(response: Response): Promise<ParsedApiPayload<T>> {
  const rawText = await response.text();
  const contentType = response.headers.get("content-type") || "";
  const preview = rawText.replace(/\s+/g, " ").trim().slice(0, 120);
  if (!rawText) {
    return {
      contentType,
      data: null,
      isHtml: contentType.includes("text/html"),
      isJson: false,
      preview,
      rawText,
    };
  }

  try {
    return {
      contentType,
      data: JSON.parse(rawText) as T & ApiErrorPayload,
      isHtml: false,
      isJson: true,
      preview,
      rawText,
    };
  } catch {
    return {
      contentType,
      data: null,
      isHtml: contentType.includes("text/html"),
      isJson: false,
      preview,
      rawText,
    };
  }
}

async function requestWithCandidateFallback<T>(candidateUrls: string[], init: RequestInit): Promise<T> {
  for (let index = 0; index < candidateUrls.length; index += 1) {
    const response = await fetch(candidateUrls[index], init);
    const payload = await readApiPayload<T>(response);
    const canRetryMismatch = !ADMIN_API_BASE_URL && index < candidateUrls.length - 1 && shouldRetryApiMismatch(response, payload);
    if (canRetryMismatch) {
      continue;
    }

    if (!payload.isJson) {
      throw new Error(buildNonJsonApiError(payload, candidateUrls.slice(0, index + 1)));
    }
    if (!response.ok) {
      throw new Error(payload.data?.msg || `HTTP ${response.status}`);
    }
    if (!payload.data) {
      throw new Error("관리자 API가 빈 응답을 반환했습니다.");
    }

    return payload.data;
  }

  throw new Error(`관리자 API 연결에 실패했습니다.${formatTriedUrls(candidateUrls)}`);
}

/**
 * 관리자 JSON API 호출 공통 함수.
 * Firebase ID 토큰을 Authorization 헤더에 붙여 응답을 JSON으로 반환한다.
 */
export async function fetchJsonWithAuth<T>(
  path: string,
  init: RequestInit,
  getIdToken: (forceRefresh?: boolean) => Promise<string>,
): Promise<T> {
  // 운영은 Hosting rewrite, 로컬 CRA 개발 서버는 setupProxy를 통해 같은 상대 경로를 유지한다.
  const token = await getIdToken();
  const headers = new Headers(init.headers || {});
  const candidateUrls = buildAdminUrlCandidates(path);

  if (token) {
    headers.set("Authorization", `Bearer ${token}`);
  }

  return requestWithCandidateFallback<T>(candidateUrls, {
    ...init,
    headers,
    cache: "no-store",
  });
}

/**
 * 관리자 multipart/form-data API 호출 공통 함수.
 * 파일 업로드 요청에 인증 토큰을 추가하고 JSON 응답을 일관된 에러 처리로 반환한다.
 */
export async function fetchFormWithAuth<T>(
  path: string,
  formData: FormData,
  getIdToken: (forceRefresh?: boolean) => Promise<string>,
  method = "PUT",
): Promise<T> {
  // 파일 업로드도 같은 /api/admin 경로를 유지하고, 환경별 라우팅만 서버 쪽에서 나눈다.
  const token = await getIdToken();
  const headers = new Headers();
  const candidateUrls = buildAdminUrlCandidates(path);

  if (token) {
    headers.set("Authorization", `Bearer ${token}`);
  }

  return requestWithCandidateFallback<T>(candidateUrls, {
    method,
    headers,
    body: formData,
    cache: "no-store",
  });
}
